import { Box, Typography, makeStyles } from '@material-ui/core';

/**
 * One theme (or hook) as a full-width card.
 *
 * Replaces the dense two-column list: a theme carries a name, a description, a
 * share, a brand split and an example caption, and cramming all of that into a
 * half-width row left every part of it illegible.
 */

const useStyles = makeStyles((theme) => ({
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
    padding: theme.spacing(2.5, 3),
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    '&:hover': {
      borderColor: theme.palette.text.disabled,
      boxShadow: '0 1px 6px rgba(15, 23, 42, 0.06)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  },
  title: { fontWeight: 700, fontSize: 20, lineHeight: 1.25 },
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    marginTop: 4,
    maxWidth: 620,
  },
  stats: { display: 'flex', gap: theme.spacing(4), flexShrink: 0 },
  stat: { textAlign: 'right' },
  statLabel: { fontSize: 12, color: theme.palette.text.secondary, whiteSpace: 'nowrap' },
  statValue: { fontWeight: 700, fontSize: 20, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums' },
  track: {
    height: 8,
    borderRadius: 4,
    background: '#e2e8f0',
    margin: theme.spacing(2.5, 0, 2),
    overflow: 'hidden',
  },
  fill: {
    height: '100%',
    borderRadius: 4,
    background: '#0f172a',
    transition: 'width 300ms ease',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  pills: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.25) },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 999,
    padding: theme.spacing(0.75, 2),
    fontSize: 14,
  },
  dot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  pillCount: { fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  quote: {
    borderLeft: '3px solid #94a3b8',
    paddingLeft: theme.spacing(1.75),
    marginTop: theme.spacing(2.5),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    fontSize: 15,
    lineHeight: 1.5,
  },
}));

function ThemeCard({
  entry,
  brandColor = () => '#94a3b8',
  onSelect,
  formatNumber = (n) => n,
  formatPercentage = (n) => `${n}%`,
}) {
  const classes = useStyles();
  if (!entry) return null;

  const byBrand = Object.entries(entry.by_brand || {}).sort((a, b) => b[1] - a[1]);
  // The best-performing post is sorted first by the classifier, so its caption
  // is the most representative single example of the theme.
  const example = (entry.example_posts || [])[0];
  const share = entry.share_of_posts || 0;

  return (
    <Box
      className={classes.card}
      role="button"
      tabIndex={0}
      aria-label={`${entry.label}: ${entry.post_count} posts, ${share.toFixed(1)}% share. Show the posts behind it.`}
      onClick={() => onSelect && onSelect(entry)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect && onSelect(entry);
        }
      }}
    >
      <Box className={classes.head}>
        <Box style={{ minWidth: 0 }}>
          <Typography className={classes.title}>{entry.label}</Typography>
          {entry.description && (
            <Typography className={classes.description}>{entry.description}</Typography>
          )}
        </Box>

        <Box className={classes.stats}>
          <Box className={classes.stat}>
            <Typography className={classes.statLabel}>Posts</Typography>
            <Typography className={classes.statValue}>
              {formatNumber(entry.post_count || 0)}
            </Typography>
          </Box>
          <Box className={classes.stat}>
            <Typography className={classes.statLabel}>Share</Typography>
            <Typography className={classes.statValue}>{share.toFixed(1)}%</Typography>
          </Box>
          <Box className={classes.stat}>
            <Typography className={classes.statLabel}>Avg. engagement</Typography>
            <Typography className={classes.statValue}>
              {formatPercentage(entry.avg_engagement_rate || 0)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.track}>
        <Box className={classes.fill} style={{ width: `${Math.min(share, 100)}%` }} />
      </Box>

      <Box className={classes.pills}>
        {byBrand.map(([brand, count]) => (
          <Box key={brand} className={classes.pill}>
            <Box className={classes.dot} style={{ backgroundColor: brandColor(brand) }} />
            <span>{brand}</span>
            <span className={classes.pillCount}>{formatNumber(count)}</span>
          </Box>
        ))}
      </Box>

      {example && example.caption_snippet && (
        <Typography className={classes.quote}>&ldquo;{example.caption_snippet}&rdquo;</Typography>
      )}
    </Box>
  );
}

export default ThemeCard;
